import { keepPreviousData, queryOptions, useQuery } from '@tanstack/react-query'
import { DateRange } from 'react-day-picker'

import * as apiClient from '@/lib/api'
import useIsQueryEnabled from '@/lib/hooks/query-enabler'
import { useQueryResetWithRefresh } from '@/lib/hooks/query-refetch-with-reset'
import { buildQueryParamsStringWithFilters } from '@/lib/utils'
import {
  ApiReport,
  ApiReportRisksOrOpportunities,
  ApiRiskOrOpportunityChoices
} from '@/modules/client-actions-report/types'
import { useDate } from '@/stores/date-filter'
import { ClientActionsReportType, ClientActionsTypes } from '@/types/enums/client-actions'
import { FetchParams, FetchParamsWithoutPagination } from '@/types/search'
import { QUERY_KEY_CLIENT_ACTIONS_REPORT, QUERY_KEY_REPORT_RISKS_OR_OPPORTUNITIES } from '@/utils/keys'

function fetchReport(date: DateRange, fetchParams: FetchParams, reportType: ClientActionsReportType): Promise<ApiReport> {
  const fetchParamsFinal = {...fetchParams, type: reportType}
  return apiClient.get(`/firm/report?${buildQueryParamsStringWithFilters(fetchParamsFinal, date, null)}`, true)
}

export const reportQueryOptions = (
  date: DateRange,
  fetchParams: FetchParams,
  reportType: ClientActionsReportType
) =>
  queryOptions({
    queryKey: [QUERY_KEY_CLIENT_ACTIONS_REPORT, date, fetchParams, reportType],
    queryFn: () => fetchReport(date, fetchParams, reportType),
    placeholderData: keepPreviousData
  })

export const useClientActionsReport = (fetchParams: FetchParams, reportType: ClientActionsReportType, tab: string | undefined) => {
  const { date } = useDate()
  const queryOptions = reportQueryOptions(date, fetchParams, reportType)
  queryOptions.enabled = useIsQueryEnabled(tab === "report")

  const query = useQuery(queryOptions)
  const { resetAndRefetch } = useQueryResetWithRefresh(queryOptions.queryKey, query.refetch)

  return {
    data: query.data?.data ?? [],
    total: query.data?.aggregate_data?.total ?? 0,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isPlaceholderData: query.isPlaceholderData,
    isError: query.isError,
    isRefetchError: query.isRefetchError,
    error: query.error,
    isRefetching: query.isRefetching,
    isSuccess: query.isSuccess,
    status: query.status,
    refetch: resetAndRefetch
  }
}

function fetchRisksOrOpportunities(date: DateRange, fetchParams: FetchParams, clientActionType: ClientActionsTypes): Promise<ApiReportRisksOrOpportunities> {
  return apiClient.get(`/firm/report/${clientActionType}?${buildQueryParamsStringWithFilters(fetchParams, date, null)}`, true)
}

export const risksOrOpportunitiesQueryOptions = (
  date: DateRange,
  fetchParams: FetchParams,
  clientActionType: ClientActionsTypes
) =>
  queryOptions({
    queryKey: [QUERY_KEY_REPORT_RISKS_OR_OPPORTUNITIES, date, fetchParams, clientActionType],
    queryFn: () => fetchRisksOrOpportunities(date, fetchParams, clientActionType),
    placeholderData: keepPreviousData
  })

export const useReportRisksOrOpportunities = (fetchParams: FetchParams, clientActionType: ClientActionsTypes,  tab: string | undefined) => {
  const { date } = useDate()
  const queryOptions = risksOrOpportunitiesQueryOptions(date, fetchParams, clientActionType)
  queryOptions.enabled = useIsQueryEnabled(tab === clientActionType)

  const query = useQuery(queryOptions)
  const { resetAndRefetch } = useQueryResetWithRefresh(queryOptions.queryKey, query.refetch)

  return {
    data: query.data?.data ?? [],
    total: query.data?.aggregate_data?.total ?? 0,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isPlaceholderData: query.isPlaceholderData,
    isError: query.isError,
    isRefetchError: query.isRefetchError,
    error: query.error,
    isRefetching: query.isRefetching,
    isSuccess: query.isSuccess,
    status: query.status,
    refetch: resetAndRefetch
  }
}

function fetchRiskOrOpportunityChoices(date: DateRange, fetchParams: FetchParamsWithoutPagination, reportType: ClientActionsReportType): Promise<ApiRiskOrOpportunityChoices> {
  const fetchParamsFinal = {...fetchParams, type: reportType}
  return apiClient.get(`/firm/report/${reportType}-choices?${buildQueryParamsStringWithFilters(fetchParamsFinal, date, null)}`, true)
}

export const riskOrOpportunityChoicesQueryOptions = (
  date: DateRange,
  fetchParams: FetchParamsWithoutPagination,
  reportType: ClientActionsReportType
) =>
  queryOptions({
    queryKey: [QUERY_KEY_CLIENT_ACTIONS_REPORT, date, fetchParams, reportType],
    queryFn: () => fetchRiskOrOpportunityChoices(date, fetchParams, reportType),
    placeholderData: keepPreviousData
  })

export const useRiskOrOpportunityChoices = (fetchParams: FetchParamsWithoutPagination, reportType: ClientActionsReportType) => {
  const { date } = useDate()
  const queryOptions = riskOrOpportunityChoicesQueryOptions(date, fetchParams, reportType)
  const query = useQuery(queryOptions)
  const { resetAndRefetch } = useQueryResetWithRefresh(queryOptions.queryKey, query.refetch)

  return {
    data: query.data?.data ?? [],
    total: query.data?.aggregate_data?.total ?? 0,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isPlaceholderData: query.isPlaceholderData,
    isError: query.isError,
    isRefetchError: query.isRefetchError,
    error: query.error,
    isRefetching: query.isRefetching,
    isSuccess: query.isSuccess,
    status: query.status,
    refetch: resetAndRefetch
  }
}